import { useState } from 'react';
import { NJButton, NJInlineMessage, NJLink, NJStatusIndicator, NJText, NJToastService } from '@engie-group/fluid-design-system-react';
import { MyRatiosCard } from './MyRatiosCard';
import { ProjectRatiosCard } from './ProjectRatiosCard';
import { StationDegreesCard } from './StationDegreesCard';
import { missingRatioCount } from './domain';
import type { DegreeDays, RatioTable as Ratios, Referential, WeatherStation } from './types';

interface Props {
  provenance: string;
  initialization?: React.ReactNode;
  initializationBlocked?: boolean;
  referential: Referential;
  /** Référentiel recalé sur ses coefficients de performance. */
  defaultRatios: Ratios;
  /** Station choisie à l'étape précédente. */
  station: WeatherStation;
  /** Degrés-jours retenus pour cette station (standards ou corrigés). */
  stationDegrees: DegreeDays;
  onStationDegreesChange: (next: DegreeDays) => void;
  /** Degrés-jours de mes ratios (saisis), et mes ratios. */
  myDegrees: DegreeDays;
  myRatios: Ratios;
  /** Degrés-jours de mes ratios tels qu'appliqués au projet. */
  appliedDegrees: DegreeDays;
  resultDegrees?: DegreeDays;
  resultStation?: WeatherStation;
  defaultDegrees: DegreeDays;
  onMyDegreesChange: (next: DegreeDays) => void;
  onMyRatiosChange: (next: Ratios) => void;
  /** Mes ratios calibrés sur la station, et le référentiel calibré sur les mêmes degrés-jours. */
  projectRatios: Ratios;
  stationRatios: Ratios;
  pending: boolean;
  /** Mes ratios ont déjà été appliqués au moins une fois. */
  applied: boolean;
  onApply: () => void;
  onRatiosValidity: (ok: boolean) => void;
  onEditingChange: (editing: boolean) => void;
  actions: React.ReactNode;
  onNext?: () => void;
  nextDisabled?: boolean;
}

/** Étape 2 (obligatoire) : mes ratios, puis leur application au projet via la station météo. */
export function StepSettings(p: Props) {
  const [myOk, setMyOk] = useState(true);
  const [stationOk, setStationOk] = useState(true);
  const [editing, setEditing] = useState(false);
  const [stationEditing, setStationEditing] = useState(false);

  const emit = (mine: boolean, station: boolean) => {
    setMyOk(mine); setStationOk(station); p.onRatiosValidity(mine && station);
  };
  const blocked = editing || stationEditing || !myOk || !stationOk || !!p.initializationBlocked;
  const disabled = blocked || !p.pending;
  const invalid = !myOk || !stationOk || !!p.initializationBlocked;
  const incomplete = p.applied && !p.pending && missingRatioCount(p.projectRatios) > 0;
  const calculationMessage = invalid ? 'Corrigez les entrées avant de calculer.'
    : editing || stationEditing ? 'Terminez la saisie avant de calculer.'
    : p.pending ? p.applied ? 'Résultats périmés : recalculez les ratios. Les tableaux conservent le dernier calcul.' : 'Calculez les ratios pour continuer.'
    : incomplete ? 'Calcul incomplet : complétez les ratios manquants.'
    : !p.applied ? 'Calculez les ratios pour continuer.'
    : 'Les ratios du projet sont à jour.';
  const calculationVariant = invalid ? 'error'
    : blocked || incomplete || (p.pending && p.applied) ? 'warning'
    : !p.applied || p.pending ? 'information' : 'success';

  return (
    <div className="pcv3-ratios-page">
      <div className="pcv3-ratios-content">
        <MyRatiosCard
          provenance={p.provenance}
          initialization={p.initialization}
          degrees={p.myDegrees}
          ratios={p.myRatios}
          defaults={p.defaultRatios}
          defaultDegrees={p.defaultDegrees}
          onDegreesChange={p.onMyDegreesChange}
          onRatiosChange={p.onMyRatiosChange}
          onValidityChange={(ok) => emit(ok, stationOk)}
          onEditingChange={(on) => { setEditing(on); p.onEditingChange(on || stationEditing); }}
        />

        <StationDegreesCard
          station={p.station}
          degrees={p.stationDegrees}
          onDegreesChange={p.onStationDegreesChange}
          onValidityChange={(ok) => emit(myOk, ok)}
          onEditingChange={(on) => { setStationEditing(on); p.onEditingChange(editing || on); }}
        />
        <section className="pcv3-ratios-calculate" aria-label="Calcul des ratios">
          <NJInlineMessage variant={calculationVariant} scale="sm" hasStatus={false} actionPosition="bottom">
            <div className="pcv3-calculation-row">
              <div className="pcv3-calculation-status">
                <NJStatusIndicator status={calculationVariant} aria-hidden="true" />
                <NJText scale="sm" role="status">{calculationMessage}</NJText>
              </div>
            <div className="pcv3-calculation-actions">
              <NJButton variant="primary" icon="calculate" disabled={disabled} onClick={() => {
                p.onApply();
                const incomplete = missingRatioCount(p.myRatios) > 0;
                NJToastService(incomplete ? 'Les valeurs renseignées ont été calculées. Complétez les ratios manquants.'
                  : 'Les trois tableaux de résultats ont été générés.', {
                  title: incomplete ? 'Calcul incomplet' : 'Calcul terminé', iconName: incomplete ? 'info' : 'check_circle',
                  dismissAfter: 6000, hasCloseIcon: true, gaugeLabel: 'Cette confirmation se ferme après 6 secondes.',
                });
              }}
                label={p.applied ? 'Recalculer les ratios du projet' : 'Calculer les ratios du projet'} />
              {p.onNext && !p.nextDisabled && p.applied && !p.pending && !blocked && !incomplete &&
                <NJLink href="#recapitulatif" icon="arrow_forward" onClick={(event) => {
                  event.preventDefault();
                  p.onNext?.();
                }}>Suivant</NJLink>}
            </div>
            </div>
          </NJInlineMessage>
        </section>
        <ProjectRatiosCard
          applied={p.applied}
          ratios={p.projectRatios}
        />
        {p.actions}
      </div>
    </div>
  );
}
